import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  TablePagination,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Thermometer, Droplets, RefreshCw, TrendingUp, ArrowUp } from 'lucide-react';

const StatCard = ({ title, value, unit, icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <Box sx={{
      p: 2.5,
      border: '1px solid', borderColor: 'divider', borderRadius: 2,
      position: 'relative', overflow: 'hidden', height: '100%',
    }}>
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        bgcolor: color,
      }} />
      <Box display="flex" alignItems="center" mb={1} sx={{ pt: 0.5 }}>
        {icon}
        <Typography variant="caption" sx={{ ml: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem', color: 'text.secondary' }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', fontSize: '1.75rem' }}>
        {value}
        <Typography component="span" variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>
          {unit}
        </Typography>
      </Typography>
    </Box>
  </motion.div>
);

export default function Esp32Dashboard() {
  const prettyDateTime = (ts) => ts.replace(/\.000Z$/, "").replace("T", " ");
  const hourKey = (ts) => ts.slice(11, 13) + ":00";
  const dateKey = (ts) => ts.slice(0, 10);

  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [range, setRange] = useState("day");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataMode, setDataMode] = useState("raw");
  const [unit, setUnit] = useState("fahrenheit");

  const [setpoint, setSetpoint] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          "https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/esp32-data"
        );
        setReadings(
          res.data.map((r) => ({
            ...r,
            rawTimestamp: r.timestamp,
            timestamp: new Date(r.timestamp),
          }))
        );
        setError(null);
      } catch {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    (async () => {
      try {
        const r = await axios.get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/settemp');
        setSetpoint(r.data.setTemp);
      } catch (e) {
        console.error('could not fetch setTemp', e);
      }
    })();
  }, []);

  const handleSetpointChange = async (newVal) => {
    setSetpoint(newVal);
    try {
      await axios.post('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/settemp', { setTemp: newVal });
    } catch (e) {
      console.error('Failed to update setTemp', e);
    }
  };

  const { tableData, summary, chartData } = useMemo(() => {
    const convertTemp = (f) => {
      const c = ((f - 32) * 5) / 9;
      return unit === "celsius"
        ? parseFloat(c.toFixed(2))
        : parseFloat(f.toFixed(2));
    };

    const now = Date.now();
    let sd = now, ed = now;
    if (range === "day") sd = now - 24 * 60 * 60 * 1000;
    else if (range === "week") sd = now - 7 * 24 * 60 * 60 * 1000;
    else if (range === "month") sd = now - 30 * 24 * 60 * 60 * 1000;
    else {
      sd = new Date(startDate).getTime();
      ed = new Date(endDate + "T23:59:59").getTime();
    }

    const fr = readings
      .filter((r) => {
        const t = r.timestamp.getTime();
        return t >= sd && t <= ed;
      })
      .sort((a, b) => b.timestamp - a.timestamp);

    const stats = {
      avgT: "N/A", minT: "N/A", maxT: "N/A",
      avgH: "N/A", minH: "N/A", maxH: "N/A",
    };
    if (fr.length) {
      const temps = fr.map((r) => convertTemp(r.temperature));
      const hums = fr.map((r) => r.humidity);
      stats.avgT = (temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(1);
      stats.minT = Math.min(...temps).toFixed(1);
      stats.maxT = Math.max(...temps).toFixed(1);
      stats.avgH = (hums.reduce((a, b) => a + b, 0) / hums.length).toFixed(1);
      stats.minH = Math.min(...hums).toFixed(1);
      stats.maxH = Math.max(...hums).toFixed(1);
    }

    if (dataMode === "raw") {
      const cd = fr
        .map((r) => ({
          label: prettyDateTime(r.rawTimestamp),
          temperature: convertTemp(r.temperature),
          humidity: parseFloat(r.humidity.toFixed(2)),
        }))
        .reverse();
      return { tableData: fr, summary: stats, chartData: cd };
    } else {
      const agg = new Map();
      fr.forEach((r) => {
        const key = range === "day"
          ? hourKey(r.rawTimestamp)
          : dateKey(r.rawTimestamp);
        if (!agg.has(key)) agg.set(key, { temps: [], hums: [] });
        agg.get(key).temps.push(r.temperature);
        agg.get(key).hums.push(r.humidity);
      });
      const table = Array.from(agg.entries()).map(([label, { temps, hums }]) => ({
        label,
        temperature: (
          temps.map((t) => convertTemp(t)).reduce((a, b) => a + b, 0) / temps.length
        ).toFixed(2),
        humidity: (
          hums.reduce((a, b) => a + b, 0) / hums.length
        ).toFixed(2),
      }));
      return { tableData: table, summary: stats, chartData: [...table].reverse() };
    }
  }, [readings, range, startDate, endDate, unit, dataMode]);

  const latest = readings.length
    ? readings.reduce((a, b) => (b.timestamp > a.timestamp ? b : a), readings[0])
    : null;

  if (loading)
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress size={32} thickness={3} />
      </Box>
    );
  if (error)
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );

  const tickInterval = Math.ceil(chartData.length / 8);

  return (
    <Box className="fade-in" sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            ESP32 Sensor Dashboard
          </Typography>
          {latest && (
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Current:</Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Thermometer size={16} style={{ color: '#ef4444' }} />
                <Typography variant="body2">
                  {(unit === "celsius" ? ((latest.temperature - 32) * 5) / 9 : latest.temperature).toFixed(2)}
                  {unit === "celsius" ? "°C" : "°F"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Droplets size={16} style={{ color: '#2383e2' }} />
                <Typography variant="body2">{latest.humidity.toFixed(2)}%</Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Button
          onClick={() => window.location.reload()}
          sx={{ minWidth: 40, p: 1, color: 'text.secondary' }}
        >
          <RefreshCw size={18} />
        </Button>
      </Box>

      {/* Thermostat Slider */}
      <Box sx={{
        p: 2.5, mb: 4,
        border: '1px solid', borderColor: 'divider', borderRadius: 2,
      }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
          Thermostat Set-Temp
        </Typography>
        <Slider
          min={50}
          max={90}
          value={setpoint}
          onChange={(_, v) => handleSetpointChange(v)}
          valueLabelDisplay="on"
          valueLabelFormat={val => `${val}°F`}
          marks={[
            { value: 50, label: '50°F' },
            { value: 90, label: '90°F' }
          ]}
          sx={{
            height: 6,
            color: '#2383e2',
            '& .MuiSlider-thumb': {
              width: 20, height: 20,
              backgroundColor: '#fff',
              border: '2px solid currentColor',
            },
          }}
        />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { title: "Avg Temp", value: summary.avgT, unit: unit === "celsius" ? "°C" : "°F", icon: <TrendingUp size={14} style={{ color: '#f59e0b' }} />, color: '#f59e0b' },
          { title: "Min Temp", value: summary.minT, unit: unit === "celsius" ? "°C" : "°F", icon: <ArrowUp size={14} style={{ color: '#ef4444', transform: 'rotate(180deg)' }} />, color: '#ef4444' },
          { title: "Max Temp", value: summary.maxT, unit: unit === "celsius" ? "°C" : "°F", icon: <ArrowUp size={14} style={{ color: '#ef4444' }} />, color: '#ef4444' },
          { title: "Avg Hum", value: summary.avgH, unit: "%", icon: <TrendingUp size={14} style={{ color: '#2383e2' }} />, color: '#2383e2' },
          { title: "Min Hum", value: summary.minH, unit: "%", icon: <Droplets size={14} style={{ color: '#6366f1', opacity: 0.6 }} />, color: '#6366f1' },
          { title: "Max Hum", value: summary.maxH, unit: "%", icon: <Droplets size={14} style={{ color: '#6366f1' }} />, color: '#6366f1' },
        ].map((cfg, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <StatCard {...cfg} />
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Box mb={3} display="flex" gap={1.5} flexWrap="wrap">
        <ToggleButtonGroup value={range} exclusive size="small" onChange={(_, v) => v && setRange(v)}>
          {["day", "week", "month", "custom"].map((val) => (
            <ToggleButton key={val} value={val} sx={{ textTransform: 'none', fontSize: '0.75rem', px: 2 }}>
              {val.charAt(0).toUpperCase() + val.slice(1)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup value={unit} exclusive size="small" onChange={(_, v) => v && setUnit(v)}>
          {["fahrenheit", "celsius"].map((val) => (
            <ToggleButton key={val} value={val} sx={{ textTransform: 'none', fontSize: '0.75rem', px: 2 }}>
              {val === "fahrenheit" ? "°F" : "°C"}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup value={dataMode} exclusive size="small" onChange={(_, v) => v && setDataMode(v)}>
          {["raw", "avg"].map((val) => (
            <ToggleButton key={val} value={val} sx={{ textTransform: 'none', fontSize: '0.75rem', px: 2 }}>
              {val.toUpperCase()}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Custom Dates */}
      {range === "custom" && (
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <TextField
            label="Start Date" type="date" size="small"
            value={startDate} onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date" type="date" size="small"
            value={endDate} onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}

      {/* Temperature Chart */}
      <Box sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Temperature Over Time</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" angle={-45} textAnchor="end" height={50} interval={tickInterval} tick={{ fontSize: 10, fontFamily: 'Inter' }} />
            <YAxis domain={["auto", "auto"]} label={{ value: `Temp (${unit === "celsius" ? "°C" : "°F"})`, angle: -90, position: "insideLeft" }} tick={{ fontSize: 10, fontFamily: 'Inter' }} />
            <Tooltip />
            <Line type="monotone" dataKey="temperature" stroke="#ef4444" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Humidity Chart */}
      <Box sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Humidity Over Time</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" angle={-45} textAnchor="end" height={50} interval={tickInterval} tick={{ fontSize: 10, fontFamily: 'Inter' }} />
            <YAxis domain={["auto", "auto"]} label={{ value: "Humidity (%)", angle: -90, position: "insideLeft" }} tick={{ fontSize: 10, fontFamily: 'Inter' }} />
            <Tooltip />
            <Line type="monotone" dataKey="humidity" stroke="#2383e2" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Data Table */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr',
          px: 2.5, py: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'action.hover',
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', color: 'text.secondary' }}>
            {dataMode === "raw" ? "Date & Time (CDT)" : range === "day" ? "Hour" : "Date"}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', color: 'text.secondary', textAlign: 'right' }}>
            Temp ({unit === "celsius" ? "°C" : "°F"})
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', color: 'text.secondary', textAlign: 'right' }}>
            Humidity (%)
          </Typography>
        </Box>

        <Box sx={{ maxHeight: 440, overflowY: 'auto' }}>
          {tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r, idx) => (
            <Box key={idx} sx={{
              display: 'grid', gridTemplateColumns: '2fr 1fr 1fr',
              px: 2.5, py: 1.25,
              borderBottom: '1px solid', borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {dataMode === "raw" ? prettyDateTime(r.rawTimestamp) : r.label}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'right', fontSize: '0.8rem' }}>
                {r.temperature}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'right', fontSize: '0.8rem' }}>
                {r.humidity}
              </Typography>
            </Box>
          ))}
        </Box>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={tableData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
        />
      </Box>
    </Box>
  );
}
